import React, { useState } from 'react';
import { Table, Button, Row, Col, Card, Modal } from 'react-bootstrap';
import { useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import AddArticleModal from './AddArticleModal'; // Importa il modale AddArticleModal
import ProductImage from "./ProductImage";
import { formatNumber } from "../utils/numberFormat";
import { formatPrezzoPane, prezzoPaneVisibile } from "../utils/mostraPrezziDestinazione";

/** Quantità iniziale da ordine minimo (qtaminima), con fallback su qtarange come in OrderTable. */
const getOrdineMinimoQuantity = (articolo, unitaMisuraFallback = 'Pz') => {
  const unita = articolo.unita_m || articolo.unita || unitaMisuraFallback;
  const isKg = String(unita).toLowerCase() === 'kg';
  let range = Number(articolo.qtarange ?? articolo.qtaRange) || 0;
  if (range === 0) range = isKg ? 0.1 : 1;
  return Number(articolo.qtaminima ?? articolo.qtaMinima) || range;
};

const ArticleTable = ({ 
  articoli, 
  unitaMisura = 'Pz', 
  inEvidenza = false,
  handleSaveArticle, // Funzione per salvare l'articolo
  specificDate,
  mappedOrders = [], // Aggiunto per filtrare gli articoli già presenti
  setMappedOrders,
  isActionsDisabled = false, // Nuova prop per disabilitare le azioni
  readOnlyWeekdayKeys = [],
  getExtraReadOnlyKeysForArticle = null,
  getRowLockedForTodayTomorrow = null, // Blocco orario di produzione per la data specifica selezionata
  currentDay = null,
  mostraPrezzi = false,
}) => {
  const { darkMode } = useContext(ThemeContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({
    codice: '',
    descriz: '',
    noteEst: '',
    immagine: '',
  });
  const [quantities, setQuantities] = useState({
    lunedi: 0,
    martedi: 0,
    mercoledi: 0,
    giovedi: 0,
    venerdi: 0,
    sabato: 0,
    domenica: 0,
  });

  // Funzione per gestire il click sull'immagine
  const handleImageClick = (codice, descriz, noteEst = '', immagine = '') => {
    setSelectedImage({ codice, descriz, noteEst, immagine: immagine || '' });
    setShowImageModal(true);
  };

  // Filtra gli articoli in base alla prop inEvidenza e esclude quelli già presenti in mappedOrders con quantità > 0
  const filteredArticoli = articoli.filter((articolo) => {
    
    // Filtro per evidenza
    const matchEvidenza = inEvidenza ? articolo.in_evidenza === true : true;
    
    // Trova l'articolo in mappedOrders
    const orderFound = mappedOrders.find(order => order.codice === articolo.cd_min);
    
    // Mostra l'articolo se:
    // 1. Non è presente in mappedOrders, OPPURE
    // 2. È presente ma ha quantità = 0
    const shouldShow = !orderFound || Number(orderFound.quantita) === 0;
    
    return matchEvidenza && shouldShow;
  });


    
  // Blocco per tempo minimo di produzione (orebloccoprod) sulla data specifica selezionata:
  // su date personalizzate/straordinarie il prodotto non deve poter essere inserito.
  const isArticleLockedByProd = (articolo) =>
    !!specificDate &&
    typeof getRowLockedForTodayTomorrow === 'function' &&
    getRowLockedForTodayTomorrow(articolo);

  const PROD_LOCK_MSG =
    "Troppo tardi per ordinare questo prodotto per la data selezionata (tempo minimo di produzione)";

  const handleInsertClick = (articolo) => {
      if (isArticleLockedByProd(articolo)) {
        return;
      }
      const mappedArticle = {
        codice: articolo.cd_min,
        descriz: articolo.descriz,
        nota: '',
        unita: articolo.unita_m || unitaMisura,
        unitaMisura: articolo.unita_m || unitaMisura,
        contropa: articolo.contro || "",
        immagine: articolo.immagine,
        schedaTecnica: articolo.scheda_tecnica || "",
        peso: articolo.peso_lo || 0,
        qtaMinima: articolo.qtaminima || 0,
        qtaRange: articolo.qtarange || 0,
        note_est: articolo.note_est || '', // Preserva le note del magazzino
        dati_articolo: articolo.dati_articolo || null,
        orebloccoprod: articolo.orebloccoprod ?? articolo.dati_articolo?.orebloccoprod,
        prezzo: articolo.prezzo || 0,
        tipo_prezzo: articolo.tipo_prezzo || null,
    };

  if (specificDate) {
      // Aggiorna direttamente mappedOrders (passa la funzione come prop dal parent)
      console.log("Inserimento a a data singola");
      setMappedOrders((prev) => {
        
        
        // Evita duplicati
        if (prev.some(order => order.codice === mappedArticle.codice)) return prev;
        const maxId = prev.length > 0 ? Math.max(...prev.map(order => order.id || 0)) : 0;
        const initialQty = getOrdineMinimoQuantity(articolo, unitaMisura);
        
        return [
          ...prev,
          {
            ...mappedArticle,
            id: maxId + 1,
            lunedi: 0,
            martedi: 0,
            mercoledi: 0,
            giovedi: 0,
            venerdi: 0,
            sabato: 0,
            domenica: 0,
            quantita: initialQty,
          }
        ];
      });
    } else {
      // Comportamento ordinario: apri il modal per inserimento quantità
      console.log("Inserimento a planning");
      setSelectedArticle(mappedArticle);
      setShowModal(true);
    }
  };

  return (
    <>
      <Table 
        responsive 
        striped 
        bordered 
        hover 
        variant={darkMode ? 'dark' : 'light'}
        className="mb-4 d-none d-lg-table"
      >
        <thead>
          <tr>
            <th>Articolo</th>
            <th>Descrizione</th>
            <th>Unità di misura</th>
            <th>Ordine Minimo</th>
            {mostraPrezzi && <th>Prezzo</th>}
            <th className="text-center">Immagine</th>
            <th className="text-center">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {filteredArticoli && filteredArticoli.length > 0 ? (
            filteredArticoli.map((articolo) => (
              <tr key={articolo.id}>
                <td>{articolo.cd_min}</td>
                <td>{articolo.descriz}</td>
                <td>{articolo.unita_m || unitaMisura}</td>
                <td>{articolo.unita_m?.toLowerCase() === 'kg' ? formatNumber(articolo.qtaminima, 3) : formatNumber(articolo.qtaminima, 0)}</td>
                {mostraPrezzi && (
                  <td>
                    {prezzoPaneVisibile(articolo.prezzo)
                      ? formatPrezzoPane(articolo.prezzo, articolo.unita_m || unitaMisura)
                      : ''}
                  </td>
                )}
                 <td className="text-center">
                  <div 
                    onClick={() =>
                      handleImageClick(
                        articolo.cd_min,
                        articolo.descriz,
                        articolo.note_est,
                        articolo.immagine
                      )
                    }
                    style={{ cursor: 'pointer' }}
                    title="Clicca per ingrandire l'immagine"
                  >
                    <ProductImage
                      codice={articolo.cd_min}
                      descriz={articolo.descriz}
                      immagine={articolo.immagine}
                    />
                  </div>
                </td>
                <td className="text-center">
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => handleInsertClick(articolo)} // Chiama il modale AddArticleModal
                    disabled={isActionsDisabled || isArticleLockedByProd(articolo)}
                    title={isArticleLockedByProd(articolo) ? PROD_LOCK_MSG : undefined}
                  >
                    Inserisci
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={mostraPrezzi ? 7 : 6} className="text-center py-3">
                Nessun articolo disponibile
              </td>
            </tr>
          )}
        </tbody>
      </Table>


      {/* Mobile cards */}
      <Row xs={2} className="g-1 d-lg-none">
        {filteredArticoli && filteredArticoli.length > 0 ? (
          filteredArticoli.map((articolo) => (
            <Col xs={6} key={articolo.id}>
              <Card 
                className={`h-100 text-center ${darkMode ? 'bg-dark text-light' : ''}`}
                style={{ fontSize: "0.80rem" }}
              >
                <Card.Body className="d-flex flex-column justify-content-between">
                  <div>
                    <div 
                      className="mb-2"
                      onClick={() =>
                      handleImageClick(
                        articolo.cd_min,
                        articolo.descriz,
                        articolo.note_est,
                        articolo.immagine
                      )
                    }
                      style={{ cursor: 'pointer' }}
                      title="Clicca per ingrandire l'immagine"
                    >
                      <ProductImage
                      codice={articolo.cd_min}
                      descriz={articolo.descriz}
                      immagine={articolo.immagine}
                    />
                    </div>
                    <div>cod: {articolo.cd_min}</div>
                    <div className="fw-bold">{articolo.descriz}</div>
                    <div>ord. minimo: {articolo.unita_m?.toLowerCase() === 'kg' ? formatNumber(articolo.qtaminima, 3) : formatNumber(articolo.qtaminima, 0)} {articolo.unita_m || unitaMisura}</div>
                    {mostraPrezzi && prezzoPaneVisibile(articolo.prezzo) && (
                      <div>
                        {formatPrezzoPane(articolo.prezzo, articolo.unita_m || unitaMisura)}
                      </div>
                    )} 
                  </div>
                  <div className="mt-2">
                    <Button
                      variant="primary"
                      className="w-100"
                      onClick={() => handleInsertClick(articolo)}
                      disabled={isActionsDisabled || isArticleLockedByProd(articolo)}
                      title={isArticleLockedByProd(articolo) ? PROD_LOCK_MSG : undefined}
                    >
                      Inserisci
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))
        ) : (
          <Col>
            <p className="text-center">Nessun articolo disponibile</p>
          </Col>
        )}
      </Row>

      {/* Modale AddArticleModal */}
      {!specificDate && (
        <AddArticleModal 
          showModal={showModal}
          setShowModal={setShowModal}
          addArticle={selectedArticle} // Passa l'articolo selezionato
          quantities={quantities}
          setQuantities={setQuantities}
          handleSaveArticle={() => handleSaveArticle(selectedArticle, quantities)} // Chiama la funzione di salvataggio
          currentDay={currentDay}
          unitaMisura={unitaMisura}
          darkMode={darkMode}
          readOnlyWeekdayKeys={readOnlyWeekdayKeys}
          getExtraReadOnlyKeysForArticle={getExtraReadOnlyKeysForArticle}
          mostraPrezzi={mostraPrezzi}
        />
      )}

      {/* Modal per visualizzare l'immagine ingrandita */}
      <Modal 
        show={showImageModal} 
        onHide={() => setShowImageModal(false)} 
        centered 
        size="lg"
        data-bs-theme={darkMode ? "dark" : "light"}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedImage.codice} - {selectedImage.descriz}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-4">
          <ProductImage
            codice={selectedImage.codice}
            descriz={selectedImage.descriz}
            immagine={selectedImage.immagine}
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
          />
          {selectedImage.noteEst && selectedImage.noteEst.trim() !== '' && (
            <div className="mt-3 p-3 bg-light rounded" style={{ textAlign: 'left' }}>
              <h6 className="mb-2 text-primary">
                <i className="fas fa-sticky-note me-2"></i>Note Magazzino:
              </h6>
              <p className="mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                {selectedImage.noteEst}
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            <i className="fas fa-times me-2"></i>Chiudi
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ArticleTable;